/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, useState, ReactElement } from "react";

import { connect, useDispatch } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";

import { SearchBox, Stack } from "office-ui-fabric-react/lib";

import { PrimaryButton } from "@fluentui/react/lib/Button";

import * as ApplicationActions from "Dataflows/Application/ApplicationAction";
import * as CommonDataActions from "Dataflows/CommonData/CommonDataAction";

import { useAuth } from "Authentication/AuthProvider";
import ApplicationsList from "Components/ApplicationsList/ApplicationsList";
import ListCounts from "Components/Common/ListCounts/ListCounts";
import { Title } from "Components/Common/Results/Result.styles";
import {
  defaultFilterValues,
  defaultListingPageSize,
  mvpApplicationFilterFields,
  rdApplicationFilterFields,
  studentsApplicationFilterFields,
  ApplicationCohortEnum,
  ApplicationTypeEnum,
  APPLICATION_TYPE,
  TechnologyAreaStatusEnum,
  urlLengthError,
  urlInvalidError,
} from "Constants/ApplicationConstants";
import { FilterValueFieldNames } from "Constants/FilterFields";
import { OperationStatus } from "Constants/OperationStatus";
import { RolesEnum } from "Constants/RolesPermissionsConstants";
import { TenantTypesEnum } from "Constants/TenantTypes";
import {
  getApplications,
  getApplicationsFilterValues,
  getCurrentPage,
  getCurrentTenant,
  getFileDownloadData,
  getFileDownloadStatus,
  getFilteredCount,
  getPageSize,
  getSearchText,
  getSortColumn,
  getSortDescending,
  isLoadingApplications,
} from "Dataflows/Application/ApplicationSelector";
import { AppState } from "Dataflows/Reducers";
import { getTechnologyAreas } from "Dataflows/TechnologyArea/TechnologyAreaAction";
import { IFilterField } from "Interfaces/IFilterField";
import { IFilterValue } from "Interfaces/IFilterValue";
import { constructCsvDownload } from "Utility/DownloadHelper";
import { getTenant } from "Utility/Utils";
import {
  setFilterToQueryParams,
  getFiltersFromQueryParams,
  removeQueryParams,
  useLZString,
  validUrlLength,
} from "Utility/QueryParamHelper";
import {
  buttonStackTokens,
  containerStackStyles,
  filterButtonStyles,
  listStackStyles,
  searchBoxStyles,
  tableStackStyles,
  PaddedFlexRow,
} from "../Common/CommonContainer.styles";
import SideBarContainer from "../SideBarContainer/SideBarContainer";
import { FlexWrapper, ErrorWrapper } from "./ApplicationsContainer.styles";
import { IApplicationsContainerProps } from "./IApplicationsContainerProps";

/**
 * Applications Container element that displays side bar filtering and result listing.
 * @FunctionComponent
 * @param {IApplicationsContainerProps} props The applications container props.
 * @returns {ReactElement<IApplicationsContainerProps>} The applications component.
 */
const ApplicationsContainer = (props: IApplicationsContainerProps): ReactElement => {
  const {
    selectedApplicationsFilterValues,
    applications,
    isLoadingApplications,
    fileDownloadStatus,
    fileDownloadData,
    currentPage,
    pageSize,
    filteredCount,
    tenant,
    currentTenant,
    sortColumn,
    sortDescending,
    actions: {
      addApplicationsFilterValue,
      downloadApplicationsToCSV,
      cleanDownloadCSV,
      removeApplicationsFilterValue,
      removeApplicationsProvinceFilterValue,
      searchApplications,
      updateApplicationsFilterValue,
      resetApplications,
    },
  } = props;

  const [searchText, setSearchText] = useState(props.searchText);

  const { account, hasRole, adminPermissions } = useAuth();
  const adminEmail = account?.userName;
  const dispatch = useDispatch();
  const canDownload = tenant === TenantTypesEnum.MLSA || tenant === TenantTypesEnum.MVP;

  const { encode, decode } = useLZString();
  const [urlError, setUrlError] = useState("");
  const getQueryParam = new URLSearchParams(window.location.search);
  const queryParamValue = getQueryParam.get("search");

  // Reset URL with error message
  const resetUrlWithError = (showError = false) => {
    setUrlError("");
    removeQueryParams("search");
    if (showError) setUrlError(urlLengthError);
  };

  // Get Query Params from URL, check aginst max-limit and decode
  const getQueryParamsRequest = (): any => {
    const response: any = { validUrl: true, request: null };
    if (queryParamValue && !validUrlLength(queryParamValue)) {
      response.validUrl = false;
    } else {
      response.request = decode(queryParamValue || "");
    }
    return response;
  };

  //Set the valid Query Params to URL and return true if successful.
  const setQueryParamsRequest = (requestBody: any): boolean => {
    const compressedText = encode(requestBody);
    if (compressedText && !validUrlLength(compressedText)) {
      resetUrlWithError(true);
      return false;
    }
    getQueryParam.set("search", compressedText);
    window.history.replaceState({}, "", `${window.location.pathname}?${getQueryParam}`);
    return true;
  };

  // Role based default filters only for MVP
  const addRoleBasedDefaultFilters = (defaultFilters: IFilterValue[]) => {
    const cohortFilter = {
      fieldName: FilterValueFieldNames.COHORT,
      value: ApplicationCohortEnum.Current,
    };

    addApplicationsFilterValue(cohortFilter);
    defaultFilters.push(cohortFilter);

    if (hasRole(RolesEnum.MVP_BUSINESS_GROUP)) {
      const technologyAreaStatusInvitedPending = {
        fieldName: FilterValueFieldNames.TECHNOLOGY_AREA_STATUS,
        value: TechnologyAreaStatusEnum.InvitedPending,
      };
      addApplicationsFilterValue(technologyAreaStatusInvitedPending);
      defaultFilters.push(technologyAreaStatusInvitedPending);

      const technologyAreaStatusDeclinedPending = {
        fieldName: FilterValueFieldNames.TECHNOLOGY_AREA_STATUS,
        value: TechnologyAreaStatusEnum.DeclinedPending,
      };
      addApplicationsFilterValue(technologyAreaStatusDeclinedPending);
      defaultFilters.push(technologyAreaStatusDeclinedPending);
    }

    if (hasRole(RolesEnum.MVP_EXECUTIVE) || hasRole(RolesEnum.MVP_TECH_AREA_VALIDATOR)) {
      adminPermissions?.adminRolePermissions
        .find((arp) => arp.tenant === TenantTypesEnum.MVP && arp.awardCategories?.length > 0)
        ?.awardCategories.forEach((ac) => {
          const awardCategoryFilter = {
            fieldName: FilterValueFieldNames.TECHNOLOGY_AREA_GROUP,
            value: ac,
          };

          addApplicationsFilterValue(awardCategoryFilter);
          defaultFilters.push(awardCategoryFilter);
        });
    }
  };

  /**
   * Call search function for entered text.
   * @method
   * @param {string} searchValue The search text.
   * @param {IFilterValue[]} selectedApplicationsFilterValues The filter values.
   * @param {number} page The page number.
   * @returns {void}
   */
  const handleSearch = (
    createQueryParams: boolean,
    searchValue: string,
    selectedApplicationsFilterValues: IFilterValue[],
    page: number,
    pageSize: number,
    sortColumn?: string,
    sortDescending?: boolean
  ): void => {
    let applicationType = ApplicationTypeEnum.ANY;
    let isValidRequestURL = true;

    // Don't filter by application type for MLSA
    if (tenant === TenantTypesEnum.MLSA) {
      applicationType = ApplicationTypeEnum.ANY;
    } else {
      const selectedApplicationTypeFilter = selectedApplicationsFilterValues.find(
        (value) => value.fieldName === APPLICATION_TYPE
      )?.value as ApplicationTypeEnum | undefined;
      applicationType = selectedApplicationTypeFilter || ApplicationTypeEnum.ANY;
    }

    // Add query parameters to the URL, specifically for the MVP tenant
    if (tenant === TenantTypesEnum.MVP && createQueryParams) {
      setUrlError("");
      const requestBody = setFilterToQueryParams(
        searchValue,
        selectedApplicationsFilterValues,
        page,
        pageSize,
        adminEmail,
        applicationType,
        sortColumn,
        sortDescending
      );
      isValidRequestURL = setQueryParamsRequest(requestBody);
    }

    if (isValidRequestURL) {
      searchApplications(
        searchValue,
        selectedApplicationsFilterValues,
        page,
        pageSize,
        adminEmail,
        applicationType,
        tenant,
        sortColumn,
        sortDescending
      );
    }
  };

  /**
   * Call to reset the filter values.
   * @method
   */
  const resetFilterValues = (isResetButtonClicked = false): void => {
    resetApplications();
    let defaultFilters = [...defaultFilterValues];

    // Set Conditional Default filters based on the current tenant and user roles
    if (tenant === TenantTypesEnum.MVP) {
      if (queryParamValue) {
        if (isResetButtonClicked) {
          resetUrlWithError();
          addRoleBasedDefaultFilters(defaultFilters);
        } else {
          const requestPayload = getQueryParamsRequest();
          if (requestPayload.validUrl && requestPayload.request) {
            defaultFilters = getFiltersFromQueryParams(requestPayload.request);
          } else {
            // when query params are invalid, reset to default filters
            resetUrlWithError();
            alert(urlInvalidError);
          }
        }
      } else {
        resetUrlWithError();
        addRoleBasedDefaultFilters(defaultFilters);
      }
    }

    setSearchText("");
    handleSearch(false, "", defaultFilters, 1, defaultListingPageSize);
  };

  useEffect(() => {
    if (isLoadingApplications) return;

    if (tenant !== currentTenant) {
      resetFilterValues();
      return;
    }

    handleSearch(
      false,
      searchText,
      selectedApplicationsFilterValues,
      currentPage,
      pageSize,
      sortColumn,
      sortDescending
    );
  }, [currentPage, pageSize, sortColumn, sortDescending]);

  const downloadCSVFile = (file: string): void => {
    constructCsvDownload(
      file,
      `${getTenant(tenant).toLowerCase()}_applications_${new Date().toLocaleString()}.csv`
    );
  };

  useEffect(() => {
    if (fileDownloadStatus === OperationStatus.Succeeded && !!fileDownloadData) {
      downloadCSVFile(fileDownloadData);
      cleanDownloadCSV();
    }
  }, [fileDownloadStatus, fileDownloadData]);

  /**
   * Call to handle the download to csv action.
   */
  const handleDownloadToCSV = (): void => {
    downloadApplicationsToCSV(searchText, selectedApplicationsFilterValues, tenant, adminEmail);
  };

  const onClear = (_: any): void => {
    handleSearch(false, "", selectedApplicationsFilterValues, 1, pageSize);
  };

  useEffect(() => {
    if (tenant === TenantTypesEnum.MVP) {
      dispatch(getTechnologyAreas(TenantTypesEnum.MVP));
      dispatch(CommonDataActions.loadCommonData());
    }
  }, []);

  const getApplicationsFilterValues = (): IFilterField[] => {
    switch (tenant) {
      case TenantTypesEnum.MLSA:
        return studentsApplicationFilterFields;
      case TenantTypesEnum.MVP:
        // If the admin only has the Executive role for the MVP Tenant, don't let them change their tech area groups / award cateogires
        if (
          hasRole(RolesEnum.MVP_EXECUTIVE) &&
          adminPermissions?.adminRolePermissions
            .filter((p) => p.tenant === TenantTypesEnum.MVP)
            .every((arp) => arp.role === RolesEnum.MVP_EXECUTIVE)
        ) {
          return mvpApplicationFilterFields.filter(
            (x) => x.key !== FilterValueFieldNames.TECHNOLOGY_AREA_GROUP
          );
        }
        return mvpApplicationFilterFields;
      case TenantTypesEnum.RD:
        return rdApplicationFilterFields;
      default:
        return [];
    }
  };

  return (
    <Stack horizontal styles={containerStackStyles}>
      <Stack.Item disableShrink>
        <SideBarContainer
          addFilterValue={addApplicationsFilterValue}
          removeFilterValue={removeApplicationsFilterValue}
          updateFilterValue={updateApplicationsFilterValue}
          removeFilterProvinceValue={removeApplicationsProvinceFilterValue}
          selectedFilterValues={selectedApplicationsFilterValues}
          filterFields={getApplicationsFilterValues()}
          urlWithParam={queryParamValue && !validUrlLength(queryParamValue) ? false : true}
        />
        <Stack horizontal tokens={buttonStackTokens}>
          <PrimaryButton
            styles={filterButtonStyles()}
            text="Apply"
            onClick={(): void => {
              handleSearch(true, searchText, selectedApplicationsFilterValues, 1, pageSize);
            }}
          />
          <PrimaryButton
            styles={filterButtonStyles()}
            text="Reset"
            onClick={(): void => resetFilterValues(true)}
          />
        </Stack>
      </Stack.Item>
      <Stack.Item grow styles={listStackStyles()}>
        <Stack styles={tableStackStyles}>
          <PaddedFlexRow>
            <Title>
              Applications{" "}
              {tenant === TenantTypesEnum.MVP && <ErrorWrapper>{urlError}</ErrorWrapper>}
            </Title>
            <FlexWrapper>
              <ListCounts total={filteredCount} loading={isLoadingApplications} />
              {!!canDownload && (
                <PrimaryButton
                  text="Download to CSV"
                  styles={filterButtonStyles()}
                  iconProps={{ iconName: "download" }}
                  onClick={handleDownloadToCSV}
                />
              )}
            </FlexWrapper>
          </PaddedFlexRow>
          <SearchBox
            placeholder="Search applications"
            value={searchText}
            onSearch={(searchValue: string): void =>
              handleSearch(false, searchValue, selectedApplicationsFilterValues, 1, pageSize)
            }
            onChange={(event?: React.ChangeEvent<HTMLInputElement>, newValue?: string): void =>
              setSearchText(newValue || "")
            }
            onClear={onClear}
            styles={searchBoxStyles}
          />
          <ApplicationsList
            data={applications}
            filteredCount={filteredCount}
            pageSize={pageSize}
            tenant={tenant}
          />
        </Stack>
      </Stack.Item>
    </Stack>
  );
};

/**
 * Maps ApplicationsContainer to redux state
 * @param {AppState} state The App state.
 */
const mapStateToProps = (state: AppState): Partial<IApplicationsContainerProps & any> => ({
  applications: getApplications(state.applicationState),
  fileDownloadData: getFileDownloadData(state.applicationState),
  fileDownloadStatus: getFileDownloadStatus(state.applicationState),
  currentPage: getCurrentPage(state.applicationState),
  searchText: getSearchText(state.applicationState),
  filteredCount: getFilteredCount(state.applicationState),
  isLoadingApplications: isLoadingApplications(state.applicationState),
  pageSize: getPageSize(state.applicationState),
  selectedApplicationsFilterValues: getApplicationsFilterValues(state.applicationState),
  currentTenant: getCurrentTenant(state.applicationState),
  sortColumn: getSortColumn(state),
  sortDescending: getSortDescending(state),
});

/**
 * Maps Component's dispatch/handler props to redux state
 * @param {Dispatch} dispatch The dispatch.
 */
const mapDispatchToProps = (
  dispatch: Dispatch<ApplicationActions.ApplicationActionTypes>
): Partial<IApplicationsContainerProps & any> => ({
  actions: {
    loadCommonData: bindActionCreators(CommonDataActions.loadCommonData, dispatch),
    addApplicationsFilterValue: bindActionCreators(
      ApplicationActions.addApplicationsFilterValue,
      dispatch
    ),
    downloadApplicationsToCSV: bindActionCreators(
      ApplicationActions.downloadApplicationsToCSV,
      dispatch
    ),
    cleanDownloadCSV: bindActionCreators(
      ApplicationActions.downloadApplicationsToCSVClean,
      dispatch
    ),
    removeApplicationsFilterValue: bindActionCreators(
      ApplicationActions.removeApplicationsFilterValue,
      dispatch
    ),
    removeApplicationsProvinceFilterValue: bindActionCreators(
      ApplicationActions.removeApplicationsFilterProvinceValue,
      dispatch
    ),
    removeApplicationsCountryFilterValue: bindActionCreators(
      ApplicationActions.removeApplicationsFilterCountryValue,
      dispatch
    ),
    searchApplications: bindActionCreators(ApplicationActions.searchApplications, dispatch),
    updateApplicationsFilterValue: bindActionCreators(
      ApplicationActions.updateApplicationsFilterValue,
      dispatch
    ),
    resetApplications: bindActionCreators(ApplicationActions.resetApplications, dispatch),
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(ApplicationsContainer);
